import React from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import "./StandardizedAIFeedback.css";

const StandardizedAIFeedback = ({ 
  feedbackData, 
  className = "" 
}) => {
  // Ensure we have valid props
  // console.log("📊 StandardizedAIFeedback received feedbackData:", {
  //   feedbackData,
  //   feedback: feedbackData?.feedback,
  //   impactOfPracticeAssignments: feedbackData?.impactOfPracticeAssignments,
  //   impactOfAIAssistant: feedbackData?.impactOfAIAssistant,
  //   assignmentTypeStruggled: feedbackData?.assignmentTypeStruggled,
  //   recommendations: feedbackData?.recommendations,
  //   practiceAssignmentText: feedbackData?.practiceAssignmentText,
  //   courseAssignmentText: feedbackData?.courseAssignmentText,
  //   testPrepText: feedbackData?.testPrepText,
  //   studentsAreaOfImprovement: feedbackData?.studentsAreaOfImprovement,
  //   timestamp: new Date().toISOString()
  // });
  const safeFeedbackData = feedbackData || {};
  const {
    feedback,
    impactOfPracticeAssignments,
    impactOfAIAssistant,
    assignmentTypeStruggled,
    recommendations,
    practiceAssignmentText,
    courseAssignmentText,
    testPrepText,
    studentsAreaOfImprovement
  } = safeFeedbackData;

  // Helper function to render content safely
  const renderContent = (content) => {
    if (!content || content.trim() === "") return null;

    // Security: sanitize markdown-rendered HTML with DOMPurify to prevent XSS.
    // Allow only standard formatting tags + classes for the AI feedback styling.
    const rawHtml = marked(content)
      .replace(/\*\*/g, "") // Remove markdown bold markers
      .replace(/\n/g, "<br>"); // Convert newlines to line breaks
    const cleanHtml = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ['br', 'p', 'b', 'i', 'em', 'strong', 'ul', 'ol', 'li', 'span', 'div', 'a', 'code', 'pre', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      ALLOWED_ATTR: ['class', 'href', 'title', 'target', 'rel'],
    });
    return (
      <div
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
        className="ai-feedback-content"
      />
    );
  };

  // Helper function to render a section
  const renderSection = (title, content) => {
    // Security: separate dynamic data from format string (no user-controlled format specifiers)
    console.log('🔍 renderSection called:', title, {
      hasContent: !!content,
      contentLength: content ? content.length : 0,
      contentPreview: content ? content.substring(0, 50) + '...' : 'null'
    });
    
    if (!content) {
      console.log(`❌ Skipping section "${title}" - no content`);
      return null;
    }
    
    // console.log(`✅ Rendering section "${title}"`);
    return (
      <div className="ai-feedback-section">
        <h3 className="ai-feedback-section-title">{title}</h3>
        {renderContent(content)}
      </div>
    );
  };

  // If we have raw feedback text, parse it for sections
  const parseRawFeedback = (rawFeedback) => {
    if (!rawFeedback) return null;
    
    const sections = [];
    
    // Define different section patterns based on feedback type
    let sectionPatterns = [];
    // console.log("rawFeedback", rawFeedback); 
    // Check if it's Event Overview format (has Key Insights)
    if (rawFeedback.includes("**Key Insights**")) {
      sectionPatterns = [
        { pattern: /\*\*Key Insights\*\*\s*/i, title: "Key Insights" },
        { pattern: /\*\*Practice Assignment\*\*\s*/i, title: "Practice Assignment" },
        { pattern: /\*\*Course Assignment\*\*\s*/i, title: "Course Assignment" },
        { pattern: /\*\*Test Prep\*\*\s*/i, title: "Test Prep" },
        { pattern: /\*\*Recommendations:\*\*\s*/i, title: "Recommendations" }
      ];
    }
    // Check if it's Overall Performance format (has Assignment Type Students Struggled In)
    else if (rawFeedback.includes("**Assignment Type Students Struggled In:**")) {
      console.log("🎯 Detected Overall Performance format");
      sectionPatterns = [
        { pattern: /\*\*Feedback:\*\*\s*/i, title: "Feedback" },
        { pattern: /\*\*Impact of Practice Assignments:\*\*\s*/i, title: "Impact of Practice Assignments" },
        { pattern: /\*\*Impact of AI Assistant:\*\*\s*/i, title: "Impact of AI Assistant" },
        { pattern: /\*\*Assignment Type Students Struggled In:\*\*\s*/i, title: "Assignment Type Students Struggled In" },
        { pattern: /\*\*Recommendations:\*\*\s*/i, title: "Recommendations" }
      ];
    }
    // Check if it's Student Single Event format (has Practice Assignment Activity)
    else if (rawFeedback.includes("**Practice Assignment Activity:**")) {
      sectionPatterns = [
        { pattern: /\*\*Feedback:\*\*\s*/i, title: "Feedback" },
        { pattern: /\*\*Practice Assignment Activity:\*\*\s*/i, title: "Practice Assignment Activity" },
        { pattern: /\*\*AI Assistant Usage:\*\*\s*/i, title: "AI Assistant Usage" },
        { pattern: /\*\*Recommendations:\*\*\s*/i, title: "Recommendations" }
      ];
    }
    // Check if it's Single Event Overview format (has Impact of Practice Assignments but no Assignment Type)
    else if (rawFeedback.includes("**Impact of Practice Assignments:**") && !rawFeedback.includes("**Assignment Type Students Struggled In:**")) {
      sectionPatterns = [
        { pattern: /\*\*Feedback:\*\*\s*/i, title: "Feedback" },
        { pattern: /\*\*Impact of Practice Assignments:\*\*\s*/i, title: "Impact of Practice Assignments" },
        { pattern: /\*\*Impact of AI Assistant:\*\*\s*/i, title: "Impact of AI Assistant" },
        { pattern: /\*\*Recommendations:\*\*\s*/i, title: "Recommendations" }
      ];
    }
    // Check if it's Student Single Event format with Low AI Assistant Used
    else if (rawFeedback.includes("**Low AI Assistant Used:**")) {
      sectionPatterns = [
        { pattern: /\*\*Feedback:\*\*\s*/i, title: "Feedback" },
        { pattern: /\*\*Low AI Assistant Used:\*\*\s*/i, title: "Low AI Assistant Used" },
        { pattern: /\*\*Recommendations:\*\*\s*/i, title: "Recommendations" }
      ];
    }
    // Default pattern for other formats (Single Overall Performance)
    else {
      sectionPatterns = [
        { pattern: /\*\*Feedback:\*\*\s*/i, title: "Feedback" },
        { pattern: /\*\*Impact of Practice Assignments:\*\*\s*/i, title: "Impact of Practice Assignments" },
        { pattern: /\*\*Impact of AI Assistant:\*\*\s*/i, title: "Impact of AI Assistant" },
        { pattern: /\*\*Assignment Type Students Struggled In:\*\*\s*/i, title: "Assignment Type Students Struggled In" },
        { pattern: /\*\*Recommendations:\*\*\s*/i, title: "Recommendations" }
 
      ];
    }
    
    // Parse each section by finding its start and end
    sectionPatterns.forEach((sectionPattern, index) => {
      const match = rawFeedback.match(sectionPattern.pattern);
      if (match) {
        // console.log(`✅ Found section: ${sectionPattern.title} at position ${match.index}`);
        const sectionStart = match.index + match[0].length;
        
        // Find the next section or end of text
        let sectionEnd = rawFeedback.length;
        
        // Look for the next section pattern
        for (let i = index + 1; i < sectionPatterns.length; i++) {
          const nextPattern = sectionPatterns[i];
          const nextMatch = rawFeedback.substring(sectionStart).match(nextPattern.pattern);
          if (nextMatch) {
            sectionEnd = sectionStart + nextMatch.index;
            break;
          }
        }
        
        // Extract the content between this section and the next
        const content = rawFeedback.substring(sectionStart, sectionEnd).trim();
        
        // Clean up the content
        const cleanedContent = content
          .replace(/^\n+/, '') // Remove leading newlines
          .replace(/\n+$/, '') // Remove trailing newlines
          .replace(/\*\*/g, '') // Remove markdown bold markers
          .trim();
        
        if (cleanedContent) {
          // console.log(`📝 Extracted content for ${sectionPattern.title}:`, cleanedContent.substring(0, 100) + '...');
          sections.push({ title: sectionPattern.title, content: cleanedContent });
        } else {
          console.log(`⚠️ No content found for ${sectionPattern.title}`);
        }
      } else {
        console.log(`❌ Pattern not found for: ${sectionPattern.title}`);
      }
    });
    
    return sections;
  };

  // Parse raw feedback first to see if we can extract structured sections
  const parsedSections = feedback ? parseRawFeedback(feedback) : null;
  
  // Debug logging
  // if (feedback) {
  //   console.log("🔍 StandardizedAIFeedback parsing feedback:", {
  //     hasFeedback: !!feedback,
  //     feedbackLength: feedback.length,
  //     containsFeedback: feedback.includes("**Feedback:**"),
  //     containsRecommendations: feedback.includes("**Recommendations:**"),
  //     containsAssignmentType: feedback.includes("**Assignment Type Students Struggled In:**"),
  //     parsedSectionsCount: parsedSections ? parsedSections.length : 0
  //   });
  // }

  // Check if we have structured data from props
  const hasStructuredData = !!(impactOfPracticeAssignments || impactOfAIAssistant || 
                           assignmentTypeStruggled || recommendations || practiceAssignmentText || 
                           courseAssignmentText || testPrepText || studentsAreaOfImprovement);

  // Check if we have any content to display
  const hasContent = !!(hasStructuredData || parsedSections || feedback);

  // Prioritize structured data over raw parsing when available
  const shouldUseStructuredData = hasStructuredData;
  console.log("shouldUseStructuredData", parsedSections && parsedSections.length > 0 && !hasStructuredData);
  const shouldUseParsedSections = parsedSections && parsedSections.length > 0 && !hasStructuredData;
  
  // // Debug logging for rendering decision
  // console.log("🎨 Rendering decision:", {
  //   hasStructuredData,
  //   shouldUseStructuredData,
  //   shouldUseParsedSections,
  //   parsedSectionsCount: parsedSections ? parsedSections.length : 0,
  //   hasContent,
  //   dataCheck: {
  //     feedback: !!feedback,
  //     studentsAreaOfImprovement: !!studentsAreaOfImprovement,
  //     impactOfPracticeAssignments: !!impactOfPracticeAssignments,
  //     impactOfAIAssistant: !!impactOfAIAssistant,
  //     assignmentTypeStruggled: !!assignmentTypeStruggled,
  //     recommendations: !!recommendations
  //   }
  // });
  
  // // Detailed data logging
  // console.log("📊 Detailed data check:", {
  //   feedback: feedback ? `"${feedback.substring(0, 100)}..."` : "null/empty",
  //   studentsAreaOfImprovement: studentsAreaOfImprovement ? `"${studentsAreaOfImprovement.substring(0, 100)}..."` : "null/empty",
  //   impactOfPracticeAssignments: impactOfPracticeAssignments ? `"${impactOfPracticeAssignments.substring(0, 100)}..."` : "null/empty",
  //   impactOfAIAssistant: impactOfAIAssistant ? `"${impactOfAIAssistant.substring(0, 100)}..."` : "null/empty",
  //   assignmentTypeStruggled: assignmentTypeStruggled ? `"${assignmentTypeStruggled.substring(0, 100)}..."` : "null/empty",
  //   recommendations: recommendations ? `"${recommendations.substring(0, 100)}..."` : "null/empty"
  // });

  // Determine feedback type for proper section rendering
  const getFeedbackType = () => {
    if (feedback) {
      if (feedback.includes("**Key Insights**")) {
        return "event_overview";
      } else if (feedback.includes("**Assignment Type Students Struggled In:**")) {
        return "overall_performance";
      } else if (feedback.includes("**Low AI Assistant Used:**")) {
        return "student_single_event";
      } else if (feedback.includes("**Impact of Practice Assignments:**") && !feedback.includes("**Assignment Type Students Struggled In:**")) {
        return "single_event_overview";
      } else {
        return "single_overall_performance";
      }
    }
    return "unknown";
  };

  const feedbackType = getFeedbackType();
  
  // Log feedback type after function is defined
  // console.log("🎯 Feedback type detected:", feedbackType);

  return (
    <div className={`standardized-ai-feedback ${className}`}>
      {/* Feedback Content */}
      <div className="ai-feedback-body">
         {!hasContent ? (
           <div className="ai-feedback-empty">
             <p>No feedback content available.</p>
           </div>
         ) : shouldUseStructuredData ? (
           // Display structured data (highest priority)
           <>
             {/* Render based on feedback type */}
             {feedbackType === "event_overview" ? (
               <>
                 {/* Event Overview Format */}
                 {renderSection("Key Insights", studentsAreaOfImprovement)}
                 {renderSection("Practice Assignment", practiceAssignmentText)}
                 {renderSection("Course Assignment", courseAssignmentText)}
                 {renderSection("Test Prep", testPrepText)}
                 {renderSection("Recommendations", recommendations || assignmentTypeStruggled)}
               </>
             ) : feedbackType === "overall_performance" ? (
               <>
                 {/* Overall Performance Format */}
                 {renderSection("Feedback", studentsAreaOfImprovement)}
                 {renderSection("Impact of Practice Assignments", impactOfPracticeAssignments)}
                 {renderSection("Impact of AI Assistant", impactOfAIAssistant)}
                 {renderSection("Assignment Type Students Struggled In", assignmentTypeStruggled)}
                 {renderSection("Recommendations", recommendations)}
               </>
             ) : feedbackType === "single_event_overview" ? (
               <>
                 {/* Single Event Overview Format */}
                 {renderSection("Feedback", studentsAreaOfImprovement)}
                 {renderSection("Impact of Practice Assignments", impactOfPracticeAssignments)}
                 {renderSection("Impact of AI Assistant", impactOfAIAssistant)}
                 {renderSection("Recommendations", recommendations || assignmentTypeStruggled)}
               </>
             ) : feedbackType === "single_overall_performance" ? (
               <>
                 {/* Single Overall Performance Format */}
                 {renderSection("Feedback", studentsAreaOfImprovement)}
                 {renderSection("Impact of Practice Assignments", impactOfPracticeAssignments)}
                 {renderSection("Impact of AI Assistant", impactOfAIAssistant)}
                 {renderSection("Recommendations", recommendations || assignmentTypeStruggled)}
               </>
             ) : (
               <>
                 {/* Fallback - render all available sections */}
                 {renderSection("Feedback", studentsAreaOfImprovement)}
                 {renderSection("Impact of Practice Assignments", impactOfPracticeAssignments)}
                 {renderSection("Impact of AI Assistant", impactOfAIAssistant)}
                 {renderSection("Assignment Type Students Struggled In", assignmentTypeStruggled)}
                 {renderSection("Recommendations", recommendations)}
                 {renderSection("Practice Assignment", practiceAssignmentText)}
                 {renderSection("Course Assignment", courseAssignmentText)}
                 {renderSection("Test Prep", testPrepText)}
               </>
             )}
           </>
         ) : shouldUseParsedSections ? (
           // Display parsed sections (when no structured data available)
           <>
             {parsedSections.map((section, index) => (
               <div key={index} className="ai-feedback-section">
                 <h3 className="ai-feedback-section-title">{section.title}</h3>
                 {renderContent(section.content)}
               </div>
             ))}
           </>
         ) : (
           // If we have raw feedback text but couldn't parse it, try to parse it as a single section
           feedback && (
             <div className="ai-feedback-section">
               <h3 className="ai-feedback-section-title">Feedback</h3>
               {renderContent(feedback)}
               
             </div>
           )
         )}
       </div>
    </div>
  );
};

export default StandardizedAIFeedback;
